import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components/macro';
import TextArea from 'components/Input/Textarea';
import DropDown from 'components/Input/dropdown/Dropdown';
import Modal from 'components/Modal';
import actions from 'state/actions';
import selectors from 'state/selectors';
import service from 'services';
import utils from 'utils';
import { useAsync } from 'react-use';
import Table, { TableColumn } from 'components/Table';
import Icon from 'components/Input/Icon';

const Section = styled.div`
  margin: 20px 0;
`;

const ResultTableContainer = styled.div`
  padding: 15px;
  max-height: 400px;
  width: 700px;
  overflow: auto;
`;

const MultipleReject = () => {
  const t = utils.intl.useTranslator();
  const dispatch = useDispatch();
  const documentsIDS = useSelector(selectors.documents.selectAllActiveDocumentsID);
  const [note, setNote] = useState('');
  const [causalOptions, setCausalOptions] = useState<{ value: string; label: string }[]>([]);
  const [selectedCausal, setSelectedCausal] = useState<string>();

  const [showResults, setShowResults] = useState(false);
  const [resultRows, setResultRows] = useState<
    {
      protocol: string;
      response: string;
      // eslint-disable-next-line camelcase
      protocol_in: string;
      responseStatus: string;
    }[]
  >([]);

  const resultColumns: TableColumn[] = [
    {
      Header: t('status'),
      accessor: 'responseStatus',
      width: 70,

      Cell: ({ value }: { value: 'OK' | 'KO' }) => (
        <span>
          <Icon
            icon={value === 'OK' ? 'check-filled' : 'exclamation-circle'}
            style={{
              color: value === 'OK' ? 'green' : 'red',
            }}
            size={'14px'}
          />
        </span>
      ),
    },
    {
      Header: t('protocol_number'),
      accessor: 'protocol_in',
      width: 140,
    },
    {
      Header: t('info'),
      accessor: 'response',
      width: 430,
      Cell: ({ value }: { value: string }) => (
        <div
          style={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {utils.app.textTranslated(value)}
        </div>
      ),
    },
  ];

  useAsync(async () => {
    try {
      const { data } = await service.getWfCausalNamesRejectForMassive();
      setCausalOptions(
        data.map(({ causalName, causalDesc }) => ({
          value: causalName,
          label: `${causalName} - ${causalDesc}`,
        })),
      );
    } catch (err) {
      console.error(err);
    }
  }, []);

  const handleConfirm = async () => {
    try {
      if (selectedCausal) {
        const { data } = await service.rejectWfTask({
          protocols: documentsIDS,
          wfCausalName: selectedCausal,
          note,
        });

        setShowResults(true);
        setResultRows(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const onNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(e.target.value);
  };

  const onCausalChange = (option: { value: string; label: string }) => {
    setSelectedCausal(option.value);
  };

  const handleResultsClose = () => {
    const successfulProtocols = resultRows
      .filter((item) => item.responseStatus === 'OK')
      .map((item) => Number(item.protocol));

    if (successfulProtocols.length > 0) {
      dispatch(actions.documents.removeRows(successfulProtocols));
    }

    dispatch(actions.documents.setActiveDocumentId([]));
    setShowResults(false);
  };

  if (showResults) {
    return (
      <>
        <Modal.Header
          title={t('document_type_change_results')}
          subtitle={t('document_type_change_results_description')}
        />
        <ResultTableContainer>
          <Table
            id="multiple-reject-table"
            columns={resultColumns}
            rows={resultRows}
            hasSort
            hasPagination
            hasExport={false}
          />
        </ResultTableContainer>
        <Modal.Footer withConfirmButton={false} closeButtonText={t('close')} cancelAction={handleResultsClose} />
      </>
    );
  }

  return (
    <>
      <Modal.Header title={t('reject')} subtitle={t('multiple-reject-desc')} />
      <Modal.Content>
        <Section>
          <DropDown
            id="causal-dropdown"
            onChange={onCausalChange}
            name="causale"
            options={causalOptions}
            placeholder={t('select-causal')}
          />
        </Section>
        <Section>
          <TextArea name="Nota" placeholder={t('note here')} onChange={onNoteChange} minHeight="62px" />
        </Section>
      </Modal.Content>
      <Modal.Footer
        confirmText={t('confirm')}
        confirmAction={handleConfirm}
        confirmDisabled={!selectedCausal}
        closeButtonText={t('close')}
        dontCloseOnConfirm
      />
    </>
  );
};

export default MultipleReject;
